import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/app/api";
import { UUID } from "crypto";
import type { PreOrder } from "@/app/types";
import { preOrderKeys } from "./keys";
import { cachePreOrderRelatedData } from "./cacheHelpers";
import { v2Api, DuplicatePreOrderError } from "@/lib/v2-api";

export { DuplicatePreOrderError } from "@/lib/v2-api";

/**
 * Hook to create a manual pre-order via V2 API.
 *
 * On duplicate (same partner + delivery date), throws DuplicatePreOrderError
 * with the existing pre-order. Pass `force: true` to bypass.
 */
export function useCreateManualPreOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      partnerId,
      orderDate,
      deliveryDate,
      createInitialFlow = false,
      documentType = 0,
      category = 0,
      preOrderNumber,
      employeeId,
      locationId,
      force = false,
    }: {
      // Nullable for partner-less workspaces (transport): a transport pre-order
      // has no header partner and no order-level delivery date. The V2 client
      // already accepts null for both; recap/purchases always pass strings.
      partnerId: string | null;
      orderDate: string;
      deliveryDate: string | null;
      createInitialFlow?: boolean;
      documentType?: number;
      /** Business category (PreOrderCategory): SALES=0, PURCHASES=1. */
      category?: number;
      preOrderNumber?: string;
      employeeId?: string | null;
      locationId?: string | null;
      force?: boolean;
    }) =>
      v2Api.preOrders.create({
        partnerId,
        orderDate,
        deliveryDate,
        createInitialFlow,
        type: documentType,
        category,
        sourceType: 3, // MANUAL
        preOrderNumber,
        employeeId,
        locationId,
        force,
      }) as unknown as Promise<PreOrder>,

    onSuccess: (newPreOrder) => {
      // Enrich partner from global cache when missing OR when only a partial
      // shell is present (V2 sometimes returns id/name/code without
      // `addresses`/`contacts`). Detect shells via `addresses === undefined`:
      // the global cache loads `include_addresses: true`, so genuinely empty
      // partners come back as `[]`, never `undefined`.
      const existingPartner = newPreOrder.partner as
        | { addresses?: unknown }
        | null
        | undefined;
      const isShell =
        !!existingPartner && existingPartner.addresses === undefined;
      if (newPreOrder.partner_id && (!newPreOrder.partner || isShell)) {
        const globalPartners = queryClient.getQueryData<any[]>([
          "partners",
          "global",
        ]);
        const partner = globalPartners?.find(
          (p: any) => p.id === newPreOrder.partner_id,
        );
        if (partner) {
          newPreOrder.partner = partner;
        }
      }

      // Enrich remaining related data (shipping_address, employee) from caches
      cachePreOrderRelatedData(queryClient, newPreOrder);

      // Cache the detail
      queryClient.setQueryData(
        preOrderKeys.detail(newPreOrder.id as UUID),
        newPreOrder,
      );

      // Insert the new pre-order directly into existing recap caches
      // instead of invalidating all queries (which triggers a full refetch)
      queryClient.setQueriesData(
        { queryKey: [...preOrderKeys.all, "recap"] },
        (old: any) => {
          if (!old || !Array.isArray(old)) return old;
          // Avoid duplicates
          if (old.some((po: any) => po.id === newPreOrder.id)) return old;
          return [...old, newPreOrder];
        },
      );
    },

    onError: (error) => {
      // DuplicatePreOrderError is handled by the caller (UI shows
      // confirmation dialog with "Create anyway" / "Open existing").
      if (error instanceof DuplicatePreOrderError) return;
      // No toast here. Sonner deduplicates by `id`, not by message — so a
      // bare `toast.error(...)` from this global handler would land next
      // to the caller's richer, id-tracked toast (see
      // `useCreateRecapOrder.submit`). Two simultaneous error toasts. The
      // caller owns the user-facing notification for the create flow.
      console.error("Failed to create manual pre-order:", error);
    },
  });
}

/**
 * Hook to create an avoir (credit note) from an existing pre-order
 */
export function useCreateAvoirFromPreOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (preOrderId: string) =>
      api.preOrders.createAvoirFromPreOrder(preOrderId),

    onSuccess: (newAvoir) => {
      cachePreOrderRelatedData(queryClient, newAvoir);
      queryClient.setQueryData(
        preOrderKeys.detail(newAvoir.id as UUID),
        newAvoir,
      );
      queryClient.setQueriesData(
        { queryKey: [...preOrderKeys.all, "recap"] },
        (old: any) => {
          if (!old || !Array.isArray(old)) return old;
          if (old.some((po: any) => po.id === newAvoir.id)) return old;
          return [...old, newAvoir];
        },
      );
    },

    onError: (error) => {
      console.error("Failed to create avoir from pre-order:", error);
    },
  });
}

/**
 * Hook to create a purchase order from an existing pre-order
 */
export function useCreatePurchaseFromPreOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (preOrderId: string) =>
      api.preOrders.createPurchaseFromPreOrder(preOrderId),

    onSuccess: (newPurchase) => {
      cachePreOrderRelatedData(queryClient, newPurchase);
      queryClient.setQueryData(
        preOrderKeys.detail(newPurchase.id as UUID),
        newPurchase,
      );
      queryClient.setQueriesData(
        { queryKey: [...preOrderKeys.all, "recap"] },
        (old: any) => {
          if (!old || !Array.isArray(old)) return old;
          if (old.some((po: any) => po.id === newPurchase.id)) return old;
          return [...old, newPurchase];
        },
      );
    },

    onError: (error) => {
      console.error("Failed to create purchase from pre-order:", error);
    },
  });
}
